import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from './pages/AppShell';
import { LoginPage } from './pages/LoginPage';
import { ResetPage } from './pages/ResetPage';
import { MyTasksPage } from './pages/MyTasksPage';
import { ProjectPage } from './pages/ProjectPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { SearchPage } from './pages/SearchPage';
import { CalendarPage } from './pages/CalendarPage';
import { InboxPage } from './pages/InboxPage';
import { HolidayPage } from './pages/HolidayPage';
import { SettingsPage } from './pages/SettingsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { api } from './lib/api';
import { keys } from './lib/queries';
import { queryClient } from './lib/queryClient';

const rootRoute = createRootRoute({
  notFoundComponent: NotFoundPage,
});

/**
 * Guards every route under the shell.
 *
 * The session is an httpOnly cookie, so the client can't inspect it — the only
 * way to know is to ask. `/me` is cheap and cached, so this costs one request
 * per session rather than one per navigation.
 */
async function requireAuth() {
  try {
    await queryClient.ensureQueryData({
      queryKey: keys.me,
      queryFn: () => api.me().then((r) => r.user),
      staleTime: 15 * 60_000,
    });
  } catch {
    throw redirect({ to: '/login' });
  }
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset',
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: ResetPage,
});

/** Layout route: everything below it renders inside the sidebar shell. */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  beforeLoad: requireAuth,
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: MyTasksPage,
});

const projectRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/projects/$projectId',
  parseParams: ({ projectId }) => ({ projectId: Number(projectId) }),
  component: ProjectPage,
});

const taskRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tasks/$taskId',
  parseParams: ({ taskId }) => ({ taskId: Number(taskId) }),
  component: TaskDetailPage,
});

const searchRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/search',
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === 'string' ? search.q : '',
  }),
  component: SearchPage,
});

const calendarRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/calendar',
  component: CalendarPage,
});

const inboxRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inbox',
  component: InboxPage,
});

const holidayRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/holidays',
  component: HolidayPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  resetRoute,
  shellRoute.addChildren([
    indexRoute,
    projectRoute,
    taskRoute,
    searchRoute,
    calendarRoute,
    inboxRoute,
    holidayRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
